/**
 * Per-metric value/delta/axis formatting + accessors. Tracker metrics are read via the
 * shared getVoluumValueForDisplay (reads or derives ratios), SMS metrics read directly.
 * For Media Devoted | Created By Ciro Autiero
 */

import { formatCurrency, formatNumber, formatPercentage, getCurrencySymbol } from '@/lib/utils';
import { getVoluumValueForDisplay } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab/shared/utils/voluum';

import type { CompareMetric } from './compareMetrics';
import type { SourceTotals, TimeSeriesBucket } from '../data/types';

function fin(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function readFrom(
  record: Record<string, number> | undefined,
  sms: { sentCount: number; amount: number } | undefined,
  metric: CompareMetric,
): number | null {
  if (metric.source === 'sms') {
    return fin((sms as Record<string, number> | undefined)?.[metric.field]);
  }
  return fin(getVoluumValueForDisplay(record, metric.field));
}

/** Resolve a metric off range totals → always a finite number (0 when absent). */
export function readMetric(totals: SourceTotals, metric: CompareMetric): number {
  const record = metric.source === 'sms' ? undefined : totals[metric.source];
  return readFrom(record, totals.sms, metric) ?? 0;
}

/** Resolve a metric off one bucket → number, or null when the bucket/value is absent. */
export function readBucketMetric(
  bucket: TimeSeriesBucket | undefined,
  metric: CompareMetric,
): number | null {
  if (!bucket) return null;
  const record = metric.source === 'sms' ? undefined : bucket[metric.source];
  return readFrom(record, bucket.sms, metric);
}

export function formatMetricValue(
  metric: CompareMetric,
  value: number,
  currency = 'USD',
): string {
  switch (metric.format) {
    case 'currency':
      return formatCurrency(value, currency);
    case 'percent':
      return formatPercentage(value);
    case 'decimal':
      return value.toFixed(2);
    default:
      return formatNumber(Math.round(value));
  }
}

/** Signed value for the Δ column ("+$1.20", "-3", "+4.5%"). */
export function formatMetricDelta(
  metric: CompareMetric,
  delta: number,
  currency = 'USD',
): string {
  const sign = delta > 0 ? '+' : '';
  return sign + formatMetricValue(metric, delta, currency);
}

export function formatDeltaPct(pct: number): string {
  if (!Number.isFinite(pct)) return '—';
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

function compact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(Math.round(value));
}

/** Short y-axis tick, e.g. "$1.2k" / "45%" / "3.4k". */
export function formatAxisTick(
  metric: CompareMetric,
  value: number,
  currency = 'USD',
): string {
  if (metric.format === 'percent') return `${Math.round(value)}%`;
  if (metric.format === 'currency') return `${getCurrencySymbol(currency)}${compact(value)}`;
  return compact(value);
}
